import { useAgencyApi, useCurrentAgency, usePageNotifications } from '.'
import { useInvokeApi } from 'hooks'
import { considerDataToBeStale } from 'utils'
import { CustomerDevice } from 'contexts/base'
import { CustomerDeviceViewModel, CustomerCertificateViewModel, } from 'api'
import { DeviceProperty } from '../api'

const SendActivationPinSuccessMessage = 'Activation key text message sent.';
const SendActivationPinFailureMessage = 'Activation key text message could not be sent. Make sure the device\'s assigned user has a valid phone number and try again.';

export const useCurrentAgencyDevices = (
  licensedAgencyId: string | undefined | null,
) => {
  const { dispatch: dispatchNotification } = usePageNotifications()
  const agencyApi = useAgencyApi()
  const invokeApi = useInvokeApi()
  const {
    state: currentAgencyContext,
    dispatch: dispatchAgencyAction,
  } = useCurrentAgency(licensedAgencyId)

  const loadDevices = async (): Promise<void> => {
    const isStale = considerDataToBeStale(
      currentAgencyContext.devices.data,
      currentAgencyContext.devices.lastApiReceiveAt,
    )
    if (!isStale) {
      return Promise.resolve()
    }

    await invokeApi(
      () => agencyApi.getDevices(),
      'An error occurred while loading devices.',
      (result) => {
        dispatchAgencyAction({
          type: 'LoadDevices',
          payload: result,
          timestamp: Date.now(),
        })
      },
    )
  }

  const addDevice = async (
    device: CustomerDevice,
  ): Promise<CustomerDeviceViewModel | null> => {
    return await invokeApi(
      () => agencyApi.addDevice(device),
      'An error occurred while adding the device.',
      (result) => {
        dispatchAgencyAction({
          type: 'AddDevice',
          payload: result,
        })

        var message = 'Device added successfully.'

        if (result.pinSentToAssignedUserSuccessfully === true)
          message += ' ' + SendActivationPinSuccessMessage

        dispatchNotification({
          type: 'SetSuccess',
          payload: message,
        })

        if (result.pinSentToAssignedUserSuccessfully === false) {
          dispatchNotification({
            type: 'SetError',
            payload: SendActivationPinFailureMessage,
          })
        }
      },
    )
  }

  const addDevices = async (
    devices: CustomerDevice[],
    onSuccess: () => void,
  ): Promise<void> => {

    var addSuccessCount = 0
    var addFailureCount = 0
    var textCount = 0

    devices.forEach(async (device) => {

      await invokeApi(
        () => agencyApi.addDevice(device),
        'An error occurred while adding the device.',
        (result) => {
          dispatchAgencyAction({
            type: 'AddDevice',
            payload: result,
          })
  
          addSuccessCount++

          if (device.sendPinToAssignedUser === true && result.pinSentToAssignedUserSuccessfully === true)
            textCount++

          if (addSuccessCount + addFailureCount == devices.length)
          {
            dispatchNotification({
              type: 'SetSuccess',
              payload: generateDeviceSuccessNotificationPayload(addSuccessCount, textCount),
            })

            onSuccess()
          }
        },
        undefined,  //onSuccess is not needed since it is handled with onResult
        () => {
          addFailureCount++

          //Partial success, still has value to the user
          if (addSuccessCount > 0 && addSuccessCount + addFailureCount == devices.length)
          {
            dispatchNotification({
              type: 'SetSuccess',
              payload: generateDeviceSuccessNotificationPayload(addSuccessCount, textCount),
            })

            onSuccess()
          }
          else if (addFailureCount == devices.length)
          {
            dispatchNotification({
              type: 'SetError',
              payload: 'An error occurred while adding the devices.',
            })
          }
        },
        false
      )
    })
  }

  const generateDeviceSuccessNotificationPayload = (deviceSuccessCount: number, textCount: number): string => {
    var message = (deviceSuccessCount == 1) ? '1 device added successfully. ' : deviceSuccessCount + ' devices added successfully. '

    if (textCount > 0)
      message += (textCount == 1) ? '1 activation key text message sent.' : textCount + ' activation key text messages sent.'

    return message
  }

  const editDevice = async (device: CustomerDevice): Promise<void> => {
    await invokeApi(
      () => agencyApi.editDevice(device),
      'An error occurred while saving the device.',
      (result) => {
        dispatchAgencyAction({
          type: 'EditDevice',
          payload: result,
        })

        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Device updated successfully.',
        })
      },
    )
  }

  const deleteDevice = async (deviceId: string): Promise<void> => {
    await invokeApi(
      () => agencyApi.deleteDevice(deviceId),
      'An error occurred while saving the device.',
      undefined,
      () => {
        dispatchAgencyAction({
          type: 'DeleteDevice',
          payload: deviceId,
        })

        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Device deleted successfully.',
        })
      },
    )
  }

  const replaceDevice = async (
    device: CustomerDevice,
    cb: (id: string) => void,
  ): Promise<void> => {
    await invokeApi(
      () => agencyApi.replaceDevice(device),
      'An error occurred replacing the device.',
      (result) => {
        dispatchAgencyAction({
          type: 'EditDevice',
          payload: result,
        })

        // call the callback prior to deleting the replaced device so that the side panel can update to show the new device
        cb(result.customerDeviceId)

        dispatchAgencyAction({
          type: 'DeleteDevice',
          payload: device.id,
        })
      },
    )
  }

  const generateDevicePin = async (
    deviceId: string,
  ): Promise<CustomerDeviceViewModel | null> => {
    const result = await invokeApi(
      () => agencyApi.generateDevicePin(deviceId),
      "An error occurred generating the device's key.",
      (result) => {
        dispatchAgencyAction({
          type: 'EditDevice',
          payload: result,
        })
      },
    )

    return result
  }

  const sendDevicePin = async (
    deviceId: string,
  ): Promise<void> => {
    await invokeApi(
      () => agencyApi.sendDevicePin(deviceId),
      "An error occurred sending the device's key.",
      (result) => {
        if (result) {
          dispatchNotification({
            type: 'SetSuccess',
            payload: SendActivationPinSuccessMessage,
          })
        }
        else {
          dispatchNotification({
            type: 'SetError',
            payload: SendActivationPinFailureMessage,
          })
        }
      },
    )
  }

const getDevicePropertyLabelByEnum = (
    device: CustomerDevice,
    prop: DeviceProperty) => 
    {
        if (device.properties === null) return '--'

        // the dictionary is keyed off of the string labels, so convert the enum int to the string
        return device.properties[DeviceProperty[prop]]
    }
  
  const copyDeviceDetails = async(device: CustomerDevice): Promise<void> =>
  {
      const textToCopy = 
`Name
${device.name}
Device
${getDevicePropertyLabelByEnum(
  device,
  DeviceProperty.DeviceManufacturer,
)} ${getDevicePropertyLabelByEnum(
  device,
  DeviceProperty.DeviceModel,
)}
OS Version
${getDevicePropertyLabelByEnum(
    device,
    DeviceProperty.OsVersion,
)}
Field Ops Version
${getDevicePropertyLabelByEnum(
    device,
    DeviceProperty.AppVersion,
)}
Device ID
${device.customerDeviceId}`;
    
      if ((navigator as any).clipboard) {
        // Modern browsers
        ;(navigator as any).clipboard.writeText(textToCopy)
          dispatchNotification({
              type: 'SetSuccess',
              payload: 'Copied.',
          })
      } else if ((window as any).clipboardData) {
        // Legacy browsers (IE)
        ;(window as any).clipboardData.setData('text', textToCopy)
          dispatchNotification({
              type: 'SetSuccess',
              payload: 'Copied.',
          })
      } else {
        // Unsupported
          dispatchNotification({
              type: 'SetSuccess',
              payload: 'Manual copy required.',
          })
      }
  }

  const getDeviceCertificateHistory = async (
    deviceId: string,
  ): Promise<CustomerCertificateViewModel[] | null> => {
    const result = await invokeApi(
      () => agencyApi.getDeviceCertificateHistory(deviceId),
      "An error occurred getting device's certificate history.",
    )
    return result
  }

  return {
    devices: currentAgencyContext.devices.data ?? [],
    deviceLookup: currentAgencyContext.deviceLookup,
    loadDevices: loadDevices,
    addDevice: addDevice,
    addDevices: addDevices,
    editDevice: editDevice,
    deleteDevice: deleteDevice,
    replaceDevice: replaceDevice,
    generateDevicePin: generateDevicePin,
    sendDevicePin: sendDevicePin,
    copyDeviceDetails: copyDeviceDetails,
    getDeviceCertificateHistory: getDeviceCertificateHistory,
    getDevicePropertyLabelByEnum: getDevicePropertyLabelByEnum
  }
}
